/* ============================================================== */
/*                                                                */
/*   mockPatientHistoryV4  ·  ข้อมูลจำลองของหน้า Patient History   */
/*   Viewer (แบบ 4d) — copy จาก <script type="text/x-dc"> ของ       */
/*   docs/design/PatientHistory.html เพื่อ ให้ mock mode ตรงกับแบบ  */
/*                                                                */
/*   pure file: ไม่ import SDK / react                              */
/*                                                                */
/* ============================================================== */

import {
  buildHistorySections,
  type HistoryDetail,
  type HistoryEncounterRow,
  type HistorySection,
  type HistoryV4Slice,
} from "./historyModelV4";

export type MockHistoryVisit = {
  no: string;
  date: string;
  out: string;
  doc: string;
  dx: string;
  pkg: boolean;
  vit: { t: string; tmp: string; pulse: string; bp: string; rr: string; o2: string; bmi: string };
  smoke: string;
  alc: string;
  allergy: string;
  curMeds: string;
  cc: string;
  pi: string;
  plan: string;
  /** [รายการ, ผล, หน่วย, ค่าอ้างอิง, flag H/L/N] */
  labs: [string, string, string, string, string][];
  /** [รายการ, Result, Impression] */
  imgs: [string, string, string][];
  /** [ยา, วิธีใช้, จำนวน] */
  meds: [string, string, string][];
  /** [รายการ, ผล / หมายเหตุ] */
  procs: [string, string][];
  note: string;
  nurse: string[];
  scans: string[];
  photos: { label: string; cap: string; at: string; by: string; mark: string; color: string }[];
  certs: { no: string; type: string; at: string; text: string }[];
};

/** ค่าคงที่จากแบบ 4d */
export const MOCK_HISTORY_NOW = "24/10/2569 11:20";
export const MOCK_HISTORY_DIVISION = "แผนกเวชศาสตร์ชะลอวัย";
export const MOCK_HISTORY_DOCTOR = "พญ. ธิดารัตน์ วัฒนา";
export const MOCK_HISTORY_SCREENED_BY = "พยาบาลตัวอย่าง 2";
export const MOCK_HISTORY_UNDERLYING = "ภูมิแพ้อากาศ";
export const MOCK_HISTORY_SURGICAL = "ผ่าตัดไส้ติ่ง (Appendectomy) · พ.ศ. 2560 · รพ.ตัวอย่าง";

export const MOCK_HISTORY_VISITS: MockHistoryVisit[] = [
  {
    no: "29/1",
    date: "15/09/2569 10:12",
    out: "15/09/2569 12:05",
    doc: MOCK_HISTORY_DOCTOR,
    dx: "Z00.0 Encounter for general medical examination",
    pkg: true,
    vit: {
      t: "15/09/2569 10:20",
      tmp: "36.6 °C",
      pulse: "72",
      bp: "118 /76",
      rr: "16",
      o2: "99",
      bmi: "21.45",
    },
    smoke: "NA",
    alc: "ดื่มสังสรรค์",
    allergy: "Penicillin (ผื่น)",
    curMeds: "-",
    cc: "ตรวจสุขภาพประจำปี (Longevity Check-up)",
    pi: "ไม่มีอาการผิดปกติ ต้องการประเมินความเสี่ยงและวางแผนดูแลสุขภาพระยะยาว",
    plan: "ติดตามผล VO₂ Max และ VISIA ตามนัด แนะนำออกกำลังกายแบบ Zone 2",
    labs: [
      ["WBC", "6.8", "×10³/µL", "4.0 – 10.0", "N"],
      ["Hemoglobin", "13.1", "g/dL", "12.0 – 16.0", "N"],
      ["HbA1c", "5.4", "%", "< 5.7", "N"],
      ["LDL", "152", "mg/dL", "< 130", "H"],
      ["hs-CRP", "3.8", "mg/L", "< 3.0", "H"],
      ["Fasting Insulin", "14.2", "µIU/mL", "2.6 – 11.1", "H"],
      ["Vitamin D (25-OH)", "24", "ng/mL", "30 – 100", "L"],
    ],
    imgs: [],
    meds: [["Vitamin D3 20,000 IU", "1 แคปซูล สัปดาห์ละครั้ง", "8 แคปซูล"]],
    procs: [["VO₂ Max Test", "ผ่านครบ ไม่มีอาการผิดปกติ"]],
    note: "ตรวจสุขภาพ ผลปกติโดยรวม Vitamin D ต่ำเล็กน้อย เริ่มเสริม D3",
    nurse: ["10:20 วัดสัญญาณชีพ ปกติ", "11:40 ติดตามหลังทำ VO₂ Max ไม่มีอาการผิดปกติ"],
    scans: ["ใบยินยอมการตรวจ_หน้า1.jpg"],
    photos: [
      {
        label: "ภาพบริเวณที่ตรวจ",
        cap: "ผื่นแขนขวา",
        at: "15/09/2569 10:12",
        by: "พญ. ตัวอย่าง",
        mark: "✎ มีการวาดทับ",
        color: "#F3D9D5",
      },
    ],
    certs: [
      {
        no: "MC-2569-000123",
        type: "ใบรับรองแพทย์ (ตรวจสุขภาพ)",
        at: "15/09/2569 11:50",
        text: "ได้ตรวจร่างกายแล้ว ไม่พบความผิดปกติที่เป็นอุปสรรคต่อการทำงาน",
      },
    ],
  },
  {
    no: "10/1",
    date: "02/08/2569 12:50",
    out: "02/08/2569 13:40",
    doc: MOCK_HISTORY_DOCTOR,
    dx: "E55.9 Vitamin D deficiency, unspecified",
    pkg: false,
    vit: {
      t: "02/08/2569 12:55",
      tmp: "36.5 °C",
      pulse: "76",
      bp: "120 /78",
      rr: "17",
      o2: "98",
      bmi: "21.50",
    },
    smoke: "NA",
    alc: "NA",
    allergy: "Penicillin (ผื่น)",
    curMeds: "Vitamin D3 20,000 IU",
    cc: "ต้องการ Vitamin drip",
    pi: "อ่อนเพลียช่วงปลายสัปดาห์ ต้องการเสริมวิตามิน",
    plan: "Vitamin C + Glutathione drip ตรวจซ้ำ Vitamin D อีก 3 เดือน",
    labs: [
      ["Vitamin D (25-OH)", "21", "ng/mL", "30 – 100", "L"],
      ["FBS", "92", "mg/dL", "70 – 99", "N"],
      ["ALT", "58", "U/L", "7 – 40", "H"],
    ],
    imgs: [],
    meds: [],
    procs: [["Vitamin C + Glutathione drip (IV)", "ให้ครบ 60 นาที ไม่มีปฏิกิริยา"]],
    note: "ให้ drip ตามแผน ผู้ป่วยทนได้ดี",
    nurse: ["13:00 เริ่มให้ drip หลังมือซ้าย", "14:00 ครบ ไม่มีอาการผิดปกติ"],
    scans: [],
    photos: [
      {
        label: "ผลตรวจภายนอก/เอกสาร",
        cap: "ผลเลือดจากรพ.อื่น",
        at: "02/08/2569 14:05",
        by: "พว. ตัวอย่าง",
        mark: "",
        color: "#DDEEF3",
      },
    ],
    certs: [],
  },
  {
    no: "29/1",
    date: "12/07/2569 09:30",
    out: "12/07/2569 10:15",
    doc: MOCK_HISTORY_DOCTOR,
    dx: "J30.4 Allergic rhinitis, unspecified",
    pkg: false,
    vit: {
      t: "12/07/2569 09:35",
      tmp: "36.7 °C",
      pulse: "78",
      bp: "116 /74",
      rr: "17",
      o2: "99",
      bmi: "21.40",
    },
    smoke: "NA",
    alc: "NA",
    allergy: "Penicillin (ผื่น)",
    curMeds: "-",
    cc: "คัดจมูก จามเช้า 3 วัน",
    pi: "อาการเป็นช่วงเช้า ไม่มีไข้",
    plan: "หลีกเลี่ยงสารก่อภูมิแพ้ ติดตามอาการ",
    labs: [],
    imgs: [],
    meds: [["Cetirizine 10 mg", "1 เม็ด วันละครั้ง ก่อนนอน", "10 เม็ด"]],
    procs: [],
    note: "ภูมิแพ้อากาศ ให้ยาแก้แพ้",
    nurse: [],
    scans: [],
    photos: [
      {
        label: "ภาพบริเวณที่ตรวจ",
        cap: "หลังส่วนล่าง",
        at: "12/07/2569 09:30",
        by: "พญ. ตัวอย่าง",
        mark: "✎ มีการวาดทับ",
        color: "#E2EBD6",
      },
    ],
    certs: [
      {
        no: "MC-2569-000087",
        type: "ใบรับรองแพทย์ (ลางาน)",
        at: "12/07/2569 10:20",
        text: "สมควรพักรักษาตัว 2 วัน ตั้งแต่วันที่ 12/07/2569",
      },
    ],
  },
  {
    no: "10/1",
    date: "15/05/2569 08:54",
    out: "15/05/2569 10:00",
    doc: MOCK_HISTORY_DOCTOR,
    dx: "M54.5 Low back pain",
    pkg: false,
    vit: {
      t: "15/05/2569 08:58",
      tmp: "36.6 °C",
      pulse: "80",
      bp: "122 /80",
      rr: "18",
      o2: "99",
      bmi: "21.60",
    },
    smoke: "NA",
    alc: "NA",
    allergy: "Penicillin (ผื่น)",
    curMeds: "-",
    cc: "ปวดหลังส่วนล่าง 2 สัปดาห์",
    pi: "ปวดมากเมื่อนั่งนาน ไม่มีชา",
    plan: "กายภาพ 5 ครั้ง หลีกเลี่ยงยกของหนัก",
    labs: [],
    imgs: [["X-ray L-spine", "Alignment ปกติ ช่อง L4-5 แคบลงเล็กน้อย", "Mild L4-5 disc space narrowing"]],
    meds: [["Ibuprofen 400 mg", "1 เม็ด วันละ 3 ครั้ง หลังอาหาร", "15 เม็ด"]],
    procs: [["กายภาพบำบัด (Hot pack + Ultrasound)", "ครั้งที่ 1"]],
    note: "ปวดหลังจากการนั่งนาน ส่งกายภาพ",
    nurse: ["09:10 ประเมินความปวด 5/10"],
    scans: ["ใบส่งตัวกายภาพ.jpg"],
    photos: [],
    certs: [],
  },
  {
    no: "29/1",
    date: "04/04/2569 15:39",
    out: "04/04/2569 16:10",
    doc: MOCK_HISTORY_DOCTOR,
    dx: "Z71.3 Dietary counselling and surveillance",
    pkg: false,
    vit: {
      t: "04/04/2569 15:42",
      tmp: "36.5 °C",
      pulse: "74",
      bp: "114 /72",
      rr: "16",
      o2: "99",
      bmi: "21.30",
    },
    smoke: "NA",
    alc: "NA",
    allergy: "Penicillin (ผื่น)",
    curMeds: "-",
    cc: "ปรึกษาเรื่องโภชนาการ",
    pi: "-",
    plan: "ปรับสัดส่วนโปรตีน นัดติดตาม 3 เดือน",
    labs: [],
    imgs: [],
    meds: [],
    procs: [],
    note: "ให้คำแนะนำโภชนาการ",
    nurse: [],
    scans: [],
    photos: [],
    certs: [],
  },
];

/** วันที่ (พ.ศ.) -> 25690915 แบบเดียวกันกับ model (mock ใช้ id เรียงลำดับจากแบบ 4d) */
const mockIdOf = (index: number): number => 9000 + index;

export function mockHistoryEncounters(): HistoryEncounterRow[] {
  return MOCK_HISTORY_VISITS.map((visit, index) => {
    const searchBlob = [
      visit.dx,
      visit.doc,
      visit.cc,
      ...visit.meds.map((med) => med[0]),
      ...visit.procs.map((proc) => proc[0]),
      ...visit.labs.map((lab) => lab[0]),
    ]
      .join(" ")
      .toLowerCase();

    return {
      id: mockIdOf(index),
      number: visit.no,
      hn: "260001",
      dateText: visit.date,
      dateNum: Number(visit.date.slice(6, 10) + visit.date.slice(3, 5) + visit.date.slice(0, 2)),
      dateIso: visit.date,
      checkoutText: visit.out,
      type: "OPD",
      divisionName: MOCK_HISTORY_DIVISION,
      doctorName: visit.doc,
      diagnosisText: visit.dx,
      packageUsed: visit.pkg,
      emrId: mockIdOf(index) + 100,
      isSecret: false,
      searchBlob,
    };
  });
}

export function mockHistoryDetail(visit: MockHistoryVisit): HistoryDetail {
  const encounter = mockHistoryEncounters().find((row) => row.diagnosisText === visit.dx && row.dateText === visit.date);

  return {
    encounter: encounter ?? {
      id: 0,
      number: visit.no,
      hn: "",
      dateText: visit.date,
      dateNum: null,
      dateIso: visit.date,
      checkoutText: visit.out,
      type: "OPD",
      divisionName: MOCK_HISTORY_DIVISION,
      doctorName: visit.doc,
      diagnosisText: visit.dx,
      packageUsed: visit.pkg,
      emrId: null,
      isSecret: false,
      searchBlob: "",
    },
    summaryRows: [
      { key: "วันที่/เวลาที่เข้าพบแพทย์", value: `Check-in: ${visit.date} น., Checkout: ${visit.out} น.` },
      { key: "Vital sign", value: visit.vit.t, center: true },
      { key: "Temperature", value: visit.vit.tmp, center: true },
      { key: "Pulse", value: visit.vit.pulse, center: true },
      { key: "Blood Pressure", value: visit.vit.bp, center: true },
      { key: "Respiratory Rate", value: visit.vit.rr, center: true },
      { key: "O2sat", value: visit.vit.o2, center: true },
      { key: "BMI", value: visit.vit.bmi, center: true },
      { key: "ประวัติสูบบุหรี่/ประวัติดื่มสุรา", value: `สูบบุหรี่ ${visit.smoke}    ดื่มสุรา ${visit.alc}` },
      { key: "ประวัติการแพ้", value: visit.allergy },
      { key: "รายการยาที่ผู้ป่วยรับประทานอยู่ในปัจจุบัน", value: visit.curMeds },
      { key: "Chief Complaint", value: visit.cc },
      { key: "Present illness", value: visit.pi },
      { key: "Diagnosis", value: visit.dx },
      { key: "แผนการรักษา", value: visit.plan },
    ],
    screeningRows: [
      { key: "โรคประจำตัว", value: MOCK_HISTORY_UNDERLYING },
      { key: "ประวัติการผ่าตัด", value: MOCK_HISTORY_SURGICAL },
      { key: "บันทึกการคัดกรองโดย", value: `${MOCK_HISTORY_SCREENED_BY} · ${visit.vit.t}` },
    ],
    labs: visit.labs.map(([name, value, unit, refValue, flag]) => ({
      name,
      value: `${value}${flag === "H" ? " ▲" : flag === "L" ? " ▼" : ""}`,
      unit,
      refValue,
      flag,
    })),
    imaging: visit.imgs.map(([name, result, impression]) => ({ name, result, impression })),
    meds: visit.meds.map(([name, howToUse, quantity]) => ({ name, howToUse, quantity })),
    procs: visit.procs.map(([name, result]) => ({ name, result })),
    nurseNotes: visit.nurse,
    /* PHV ไม่ได้มีหัวข้อ Multidisciplinary ใน design bundle (4d) -> mock ไม่มีแถว หัวข้อจึงถูกซ่อน */
    consult: [],
    pictures: visit.photos.map((photo, index) => ({
      id: index + 1,
      label: photo.label,
      caption: photo.cap,
      meta: `${photo.at} · ${photo.by}`,
      mark: photo.mark === "" ? "🖼" : photo.mark,
      url: "",
    })),
    files: [],
    certificates: visit.certs.map((cert) => ({
      id: Number(cert.no.slice(-3)),
      certificate: true,
      name: `${cert.no}.pdf`,
      meta: `${cert.type} · ออกเมื่อ ${cert.at} · ${visit.doc}`,
    })),
    partialErrors: [],
  };
}

/** slice จำลองที่หน้า PHV ใช้ตอนยังไม่มีข้อมูลจริง (ตรงกับแบบ 4d: 5 encounter ล่าสุด) */
export function mockHistorySlice(): HistoryV4Slice {
  const encounters = mockHistoryEncounters();
  return {
    sequenceIndex: "Action",
    ids: {
      patientId: 1,
      encounterId: encounters[0]?.id,
      emrId: encounters[0]?.emrId ?? undefined,
    },
    filter: { period: "all", search: "", opd: true, ipd: true, from: "", to: "", division: "", doctor: "" },
    encounters,
    total: encounters.length,
    selectedId: encounters[0]?.id ?? null,
    detailFor: encounters[0]?.id ?? null,
    detail: mockHistoryDetail(MOCK_HISTORY_VISITS[0]),
    detailErrors: [],
  };
}

export const mockHistorySections = (visit: MockHistoryVisit): HistorySection[] =>
  buildHistorySections(mockHistoryDetail(visit));

/** ข้อความ "วันนี้" ของ mock (แบบ 4d fix REF = 25691027) */
export const MOCK_HISTORY_TODAY_NUM = 25691027;
